import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ContentType } from '../content/contracts';
/** Mat Bot (spec §11) — keyword search, not an LLM and not a chat. */
export const SearchHit = z.object({
    entityType: z.string(),
    entityId: z.string().uuid(),
    title: z.string(),
    /** THEIR segment. Our route is looked up from it, never built. */
    path: z.string(),
    premium: z.boolean(),
    /**
     * Whether this reader is locked out of the hit. Both ways here, like Home's
     * rail — and a non-content hit (a room, a post, a practitioner) answers
     * `false`: those have their own access rule and the content paywall was
     * never asked about them.
     */
    locked: z.boolean().optional(),
    /**
     * Joined from the content row for the page returned, not indexed — a price
     * written into the search documents would go stale the day somebody
     * repriced and nothing would say so. Null on everything not sold on its own.
     */
    priceCents: z.number().int().nonnegative().nullable().optional(),
    rank: z.number(),
    coverUrl: z.string().nullable(),
    /** Null for a room or a practitioner — "not content", never an invented type. */
    contentType: ContentType.nullable(),
    matched: z.array(z.enum(['title', 'body', 'author'])),
    snippet: z.string().optional(),
});
export const SearchFacet = z.object({
    entityType: z.string(),
    count: z.number().int().nonnegative(),
});
export const ContentFacet = z.object({
    contentType: ContentType,
    count: z.number().int().nonnegative(),
});
/**
 * Shown ABOVE results, never instead of them.
 *
 * Someone searching in distress is still searching; withholding results would
 * make the product least useful at the moment it matters most.
 */
export const DistressBanner = z.object({
    category: z.enum(['DISTRESS', 'CRISIS', 'WELLBEING']),
    matchedPhrase: z.string(),
    resources: z.array(z.object({
        key: z.string(),
        name: z.string(),
        description: z.string().nullable(),
        phone: z.string().nullable(),
        url: z.string().nullable(),
    })),
});
export const SearchResults = z.object({
    query: z.string(),
    total: z.number().int().nonnegative(),
    items: z.array(SearchHit),
    /**
     * TWO arrays, read together — not one widened array.
     *
     * `facets` is one row per entity type. `contentFacets` breaks the
     * `content_item` row down by content type. Adding `contentType` to `facets`
     * would have silently changed what that array means — several rows all
     * reading `entityType: "content_item"` — and a client keying a map on
     * `entityType` would have kept the last one and shown a wrong number with
     * nothing failing. The sum of `contentFacets` equals the `content_item` row.
     */
    facets: z.array(SearchFacet),
    contentFacets: z.array(ContentFacet),
    banner: DistressBanner.nullable(),
    /** True when stemming found nothing and trigram similarity answered instead. */
    usedFallback: z.boolean(),
});
/**
 * The Mat Bot page's own read — `GET /search/matbot`, grouped where `/search`
 * is flat. Concept expansion with receipts: `matchedOn` is the chips on the
 * « MATCHED ON … » line, and each hit's `matchedConcepts` keys into it — the
 * label is resolved once, from the line, never per row.
 */
export const MatchedConcept = z.object({
    key: z.string(),
    label: z.string(),
});
export const MatBotHit = SearchHit.extend({
    matchedConcepts: z.array(z.string()),
});
export const MatBotGroupKey = z.enum([
    'courses',
    'readings',
    'case_studies',
    'audio_podcasts',
    'printables',
    'community',
    'live_replays',
    'practitioners',
    'anchoring_phrases',
]);
export const MatBotGroup = z.object({
    key: MatBotGroupKey,
    /** Over the WHOLE match set — `items` caps at `perGroup`, this does not. */
    count: z.number().int().nonnegative(),
    items: z.array(MatBotHit),
});
export const MatBotResults = z.object({
    query: z.string(),
    total: z.number().int().nonnegative(),
    matchedOn: z.array(MatchedConcept),
    bestMatch: MatBotHit.nullable(),
    groups: z.array(MatBotGroup),
    banner: DistressBanner.nullable(),
    usedFallback: z.boolean(),
});
/**
 * `group` asks for one group in full — up to a hundred rows of it — which is
 * how a screen honours « voir les 35 autres » instead of printing a count it
 * cannot show. Without it, five per group: the overview.
 *
 * A screen keeps BOTH: the overview for the rail and the robot line (a group
 * fetch only counts its own group), the group fetch for the rows. `enabled`
 * lets it park the second one until somebody picks a group.
 */
export function useMatBot(http, q, locale, group, enabled = true) {
    return useQuery({
        queryKey: ['matbot', q, locale, group ?? null],
        enabled: enabled && q.trim().length > 0,
        queryFn: () => http.get('/search/matbot', {
            query: group ? { q, group, perGroup: 100 } : { q },
            schema: MatBotResults,
            locale,
        }),
    });
}
export function useSearch(http, q, contentTypes, locale) {
    return useQuery({
        queryKey: ['search', q, contentTypes, locale],
        enabled: q.trim().length > 0,
        queryFn: () => http.get('/search', {
            // Repeats the key: `?contentTypes=a&contentTypes=b`. A one-element
            // array serialises to a bare string, which is the case that broke
            // `?types=` once already.
            query: { q, ...(contentTypes.length ? { contentTypes } : {}) },
            schema: SearchResults,
            locale,
        }),
    });
}
//# sourceMappingURL=search.js.map